import { useEffect } from 'react'
import { Route, Routes, Navigate } from 'react-router'
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import Signin from './Signin'
import Signup from './Signup'
import VerifyEmail from './VerifyEmail'
import Welcome from './Welcome'
import ProfileCompletion from './ProfileCompletion'
import TermsAndConditions from './TermsAndConditions'
import Home from './Home'
import Account from './Account'
import AccreditationSubmission from './AccreditationSubmission'
import RequalifyGuide from './RequalifyGuide'
import AdminAccreditations from './AdminAccreditations'
import CreateDive from './CreateDive'
import Discovery from './Discovery'
import DiveDetail from './DiveDetail'
import ApprovalScreen from './ApprovalScreen'
import Payment from './Payment'
import AdminPanel from './AdminPanel'
import Support from './Support'
import PublicProfile from './PublicProfile'
import routes from './routes'
import { useSessionStore } from '../state'
import AuthGuard from '../components/AuthGuard'
import AdminGuard from '../components/AdminGuard'
import NavigationShell from '../components/NavigationShell'

import { serializeProfile } from '../utils/serializeProfile'

const App = () => {
  const setSession = useSessionStore(s => s.setSession)
  const setProfile = useSessionStore(s => s.setProfile)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSession({ isAuthenticating: false, user: null })
    }, 10000)

    const unsubscribe = onAuthStateChanged(getAuth(), async resultUser => {
      clearTimeout(timeout)

      if (resultUser) {
        const userDoc = await getDoc(
          doc(getFirestore(), 'users', resultUser.uid)
        )
        const profile = userDoc.exists()
          ? serializeProfile(userDoc.id, userDoc.data()!)
          : null

        // Check accountStatus — reject sign-in if suspended or banned
        if (
          profile &&
          (profile.accountStatus === 'suspended' ||
            profile.accountStatus === 'banned')
        ) {
          await signOut(getAuth())
          setSession({ isAuthenticating: false, user: null })
          setProfile(null)
          const statusMsg =
            profile.accountStatus === 'suspended'
              ? 'Your account has been suspended. Please contact support.'
              : 'Your account has been banned. Please contact support.'
          // Store message for the sign-in page to display
          sessionStorage.setItem('accountStatusError', statusMsg)
          return
        }

        setSession({
          isAuthenticating: false,
          user: {
            uid: resultUser.uid,
            email: resultUser.email,
            displayName: resultUser.displayName,
            photoURL: resultUser.photoURL ?? null,
            emailVerified: resultUser.emailVerified,
            providerId: resultUser.providerData[0]?.providerId ?? 'password'
          }
        })
        setProfile(profile)
      } else {
        setSession({ isAuthenticating: false, user: null })
        setProfile(null)
      }
    })

    return () => {
      clearTimeout(timeout)
      unsubscribe()
    }
  }, [setSession, setProfile])

  return (
    <Routes>
      {/* Public routes — no shell */}
      <Route path={routes.welcome} element={<Welcome />} />
      <Route path={routes.signin} element={<Signin />} />
      <Route path={routes.signup} element={<Signup />} />
      <Route path={routes.verifyEmail} element={<VerifyEmail />} />
      <Route path={routes.completeProfile} element={<ProfileCompletion />} />
      <Route path={routes.terms} element={<TermsAndConditions />} />
      <Route path={routes.termsOfService} element={<TermsAndConditions />} />

      {/* Authenticated routes — wrapped by NavigationShell */}
      <Route
        element={
          <AuthGuard>
            <NavigationShell />
          </AuthGuard>
        }
      >
        <Route path={routes.home} element={<Home />} />
        <Route path={routes.dives} element={<Discovery />} />
        <Route path={routes.diveDetail} element={<DiveDetail />} />
        <Route path={routes.diveRequests} element={<ApprovalScreen />} />
        <Route path={routes.createDive} element={<CreateDive />} />
        <Route
          path={routes.accreditation}
          element={<AccreditationSubmission />}
        />
        <Route path={routes.requalifyGuide} element={<RequalifyGuide />} />
        <Route path={routes.account} element={<Account />} />
        <Route
          path={routes.adminAccreditations}
          element={<AdminAccreditations />}
        />
        <Route path={routes.payment} element={<Payment />} />
        <Route path={routes.profile} element={<PublicProfile />} />
        <Route path={routes.support} element={<Support />} />
      </Route>

      {/* Admin routes — wrapped by AdminGuard */}
      <Route
        path={routes.admin}
        element={
          <AdminGuard>
            <AdminPanel />
          </AdminGuard>
        }
      />

      <Route path='*' element={<Navigate to={routes.welcome} />} />
    </Routes>
  )
}

export default App
